import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EntityList, PageHead } from '../../components/EntityList';
import DebtEditor from './DebtEditor';
import { useLabels } from '../../hooks/useLabels';
import { useEditorReturn } from '../../hooks/useEditorReturn';
import { normalizeReturnTo } from '../../utils/editorReturn';

function AddDebt() {
	const { t } = useLabels();
	const navigate = useNavigate();
	const { id } = useParams();
	const { pathname } = useLocation();
	const [searchParams] = useSearchParams();
	const returnTo = searchParams.get('returnTo');
	const { onReturn, returnLabel } = useEditorReturn();

	// Precise per-route prefixes (see App.js) — not a substring test, so a
	// future path segment that happens to contain "account"/"contract" can't
	// accidentally match.
	const preloadAccountId = pathname.startsWith('/AdminScreen/accounts/account/') ? id : null;
	const preloadContractId = pathname.startsWith('/AdminScreen/contracts/contract/') ? id : null;
	const preloadAssetSaleId = pathname.startsWith('/AdminScreen/assetSales/assetSale/') ? id : null;

	// Contextual parent, else the list — mirrors EditDebt.jsx's returnTo.
	const backTo = preloadAccountId
		? `/AdminScreen/accounts/account/${preloadAccountId}`
		: preloadContractId
			? `/AdminScreen/contracts/contract/${preloadContractId}`
			: preloadAssetSaleId
				? `/AdminScreen/assetSales/assetSale/${preloadAssetSaleId}`
				: '/AdminScreen/debts';

	const breadcrumb = preloadAccountId
		? [
			{ label: 'Inicio', to: '/AdminScreen' },
			{ label: t('entity.account.plural'), to: '/AdminScreen/accounts' },
			{ label: 'Cuenta', to: `/AdminScreen/accounts/account/${preloadAccountId}` },
			{ label: `Nueva ${t('entity.debt')}` },
		]
		: preloadContractId
			? [
				{ label: 'Inicio', to: '/AdminScreen' },
				{ label: t('entity.contract.plural'), to: '/AdminScreen/contracts' },
				{ label: 'Contrato', to: `/AdminScreen/contracts/contract/${preloadContractId}` },
				{ label: `Nueva ${t('entity.debt')}` },
			]
			: preloadAssetSaleId
				? [
					{ label: 'Inicio', to: '/AdminScreen' },
					{ label: t('entity.assetSale.plural'), to: '/AdminScreen/assetSales' },
					{ label: 'Venta de activo', to: `/AdminScreen/assetSales/assetSale/${preloadAssetSaleId}` },
					{ label: `Nueva ${t('entity.debt')}` },
				]
				: [
					{ label: 'Inicio', to: '/AdminScreen' },
					{ label: t('entity.debt.plural'), to: '/AdminScreen/debts' },
					{ label: `Nueva ${t('entity.debt')}` },
				];

	// returnTo wins over the contextual parent so cancelling lands back on the
	// exact origin (e.g. an account's Deudas tab), not the account's default tab.
	const handleCancel = () => navigate(normalizeReturnTo(returnTo) || backTo);
	const handleSaved = (savedDebtId) => {
		if (returnTo) {
			const target = new URL(returnTo, window.location.origin);
			target.searchParams.set('debtId', savedDebtId);
			navigate(`${target.pathname}${target.search}`);
			return;
		}
		if (preloadAssetSaleId) {
			navigate(`${backTo}/deudas`);
			return;
		}
		navigate(`/AdminScreen/debts/debt/${savedDebtId}`);
	};

	return (
		<EntityList>
			<Helmet>
				<title>{`${t('action.add')} ${t('entity.debt')}`}</title>
			</Helmet>
			<PageHead
				breadcrumb={breadcrumb}
				title={`Nueva ${t('entity.debt')}`}
				subtitle={`Completá los datos principales de la ${t('entity.debt').toLowerCase()}.`}
			/>
			<DebtEditor
				mode='create'
				onCancel={handleCancel}
				onSaved={handleSaved}
				onReturn={onReturn}
				returnLabel={returnLabel}
				saveLabel='Agregar'
				preloadAccountId={preloadAccountId}
				preloadContractId={preloadContractId}
				preloadAssetSaleId={preloadAssetSaleId}
			/>
		</EntityList>
	);
}
export default AddDebt;
